import { createBrowserRouter, Navigate } from 'react-router-dom'

import { AutoDeploymentPage } from '@/pages/auto-deploy'
import { DeploymentCreateSubPage } from '@/pages/auto-deploy/create'
import { AutoScalingListPage } from '@/pages/auto-scaling'
import { ScalingCreateSubPage } from '@/pages/auto-scaling/create'
import { ScalingMonitorDetailSubPage } from '@/pages/auto-scaling/monitor'
import { NotFoundPage } from '@/pages/not-found'
import { AdminDataView } from '@/pages/teams/data-mgr/admin'
import { MemberDataView } from '@/pages/teams/data-mgr/member'
import { MemberDataDetailPage } from '@/pages/teams/data-mgr/member-detail'
import { SharedDataView } from '@/pages/teams/data-mgr/shared'

import { AuthLayout } from './components/AuthLayout'
import { Layout } from './components/Layout'
import { PaymentCallbackLayout } from './components/PaymentCallbackLayout'
import { ProtectedTeamRoute } from './components/ProtectedTeamRoute'
import { TeamLayout } from './components/TeamLayout'
import { TeamRole } from './consts/team'
import { BillingPage } from './pages/billing'
import { CreateInstancePage } from './pages/create-instance'
import { CustomImagePage } from './pages/custom-image'
import { DataMgrPage } from './pages/data-mgr'
import { DevPage } from './pages/dev'
import { EIPListPage } from './pages/eip'
import { ACLRulesPage } from './pages/eip/acl'
import { EIPL3RuleFormPage } from './pages/eip/acl/l3-form'
import { EIPL4RuleFormPage } from './pages/eip/acl/l4-form'
import { ErrorPage } from './pages/error'
import { InstanceListPage } from './pages/instance-list'
import { MonitorDetailPage } from './pages/instance-monitoring/MonitorDetailPage'
import { InvoicePage } from './pages/invoice'
import { LoginPage } from './pages/login'
import { MeshClusteringListPage } from './pages/mesh-clustering'
import { ModelRoutePage } from './pages/modelRoute'
import { RouteBillingDetailPage } from './pages/modelRoute/billing-detail'
import { NewModelRoutePage } from './pages/modelRoute/create'
import { RouteMonitorDetailPage } from './pages/modelRoute/monitor-detail'
import { PaypalPaymentCallbackPage } from './pages/payment/paypal/callback'
import { PaypalPaymentCancelPage } from './pages/payment/paypal/cancel'
import { PrivateRegionPage } from './pages/private-region'
import { ComputeView } from './pages/private-region/compute'
import { DashboardView } from './pages/private-region/dashboard'
import { NodeMonitorView } from './pages/private-region/monitor'
import { ProfilePage } from './pages/profile'
import { AccessKeysPage } from './pages/profile/AccessKeysPage'
import { RegisterPage } from './pages/register'
import { SnapshotListPage } from './pages/snapshots'
import { SpaceMgrPage } from './pages/space-mgr'
import { TeamBillingPage } from './pages/teams/billing'
import { TeamCreateInstancePage } from './pages/teams/create-instance'
import { TeamHomePage } from './pages/teams/home'
import { AdminInstanceView } from './pages/teams/instances/admin'
import { AdminInstanceDetailPage } from './pages/teams/instances/detail'
import { MemberInstanceView } from './pages/teams/instances/member'
import { JoinTeamPage } from './pages/teams/join'
import { TeamLoginPage } from './pages/teams/login'
import { MemberPage } from './pages/teams/member/index'
import { MemberDetailPageWrapper } from './pages/teams/member/index'
import { TeamMemberPermissionPage } from './pages/teams/member/permission'
import { TeamPermissionPage } from './pages/teams/permissionQuota/TeamPermissionPage'
import { TeamSettingPage } from './pages/teams/setting'
import { AdminSnapshotView } from './pages/teams/snapshots/admin'
import { SnapshotDetailPage } from './pages/teams/snapshots/detail'
import { MemberSnapshotView } from './pages/teams/snapshots/member'
import { MemberSnapshotDetailPage } from './pages/teams/snapshots/member-detail'
import { SharedSnapshotView } from './pages/teams/snapshots/shared'
import { AdminSpaceView } from './pages/teams/space/admin'
import { MemberSpaceView } from './pages/teams/space/member'
import { TeamMemberSpaceDetailPage } from './pages/teams/space/member-detail'
import { TeamSharedSpaceView } from './pages/teams/space/shared'
import { XFollowCallbackPage } from './pages/user/callback/x/follow'
import { WelcomePage } from './pages/welcome'
import { Root } from './Root'
import { teamSwitchLoader } from './utils/teamSwitchLoader'

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Root />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/join-team',
        element: <JoinTeamPage />,
      },
      {
        path: '/team/login',
        element: <TeamLoginPage />,
      },
      {
        path: '/user/callback/x/follow',
        element: <XFollowCallbackPage />,
      },
      {
        path: '/welcome',
        element: <WelcomePage />,
      },
      {
        path: 'team',
        loader: teamSwitchLoader,
        element: <TeamLayout />,
        children: [
          { index: true, element: <Navigate to="home" replace /> },
          { path: 'home', element: <TeamHomePage /> },
          { path: 'create', element: <TeamCreateInstancePage /> },
          {
            path: 'data',
            children: [
              {
                index: true,
                element: <Navigate to="member" replace />,
              },
              {
                path: 'member',
                element: <MemberDataView />,
              },
              {
                path: 'member/:memberId',
                element: (
                  <ProtectedTeamRoute allowedRoles={[TeamRole.Owner, TeamRole.Admin]}>
                    <MemberDataDetailPage />
                  </ProtectedTeamRoute>
                ),
              },
              {
                path: 'admin',
                element: (
                  <ProtectedTeamRoute allowedRoles={[TeamRole.Owner, TeamRole.Admin]}>
                    <AdminDataView />
                  </ProtectedTeamRoute>
                ),
              },
              {
                path: 'shared',
                element: <SharedDataView />,
              },
            ],
          },
          {
            path: 'member',
            element: (
              <ProtectedTeamRoute allowedRoles={[TeamRole.Owner, TeamRole.Admin]}>
                <MemberPage />
              </ProtectedTeamRoute>
            ),
          },
          {
            path: 'member/:id',
            element: (
              <ProtectedTeamRoute allowedRoles={[TeamRole.Owner, TeamRole.Admin]}>
                <MemberDetailPageWrapper />
              </ProtectedTeamRoute>
            ),
          },
          {
            path: 'member/:id/permission',
            element: (
              <ProtectedTeamRoute allowedRoles={[TeamRole.Owner, TeamRole.Admin]}>
                <TeamMemberPermissionPage />
              </ProtectedTeamRoute>
            ),
          },
          {
            path: 'permission-quota',
            element: (
              <ProtectedTeamRoute allowedRoles={[TeamRole.Owner, TeamRole.Admin]}>
                <TeamPermissionPage />
              </ProtectedTeamRoute>
            ),
          },
          { path: 'billing', element: <TeamBillingPage /> },
          {
            path: 'setting',
            element: (
              <ProtectedTeamRoute allowedRoles={[TeamRole.Owner, TeamRole.Admin]}>
                <TeamSettingPage />
              </ProtectedTeamRoute>
            ),
          },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'profile/access-keys', element: <AccessKeysPage /> },
          {
            path: 'instances',
            children: [
              {
                index: true,
                element: <Navigate to="member" replace />,
              },
              {
                path: 'member',
                element: <MemberInstanceView />,
              },
              {
                path: 'admin',
                element: (
                  <ProtectedTeamRoute allowedRoles={[TeamRole.Owner, TeamRole.Admin]}>
                    <AdminInstanceView />
                  </ProtectedTeamRoute>
                ),
              },
              {
                path: 'detail/:instanceId',
                element: (
                  <ProtectedTeamRoute allowedRoles={[TeamRole.Owner, TeamRole.Admin]}>
                    <AdminInstanceDetailPage />
                  </ProtectedTeamRoute>
                ),
              },
              {
                path: 'monitor',
                element: <MonitorDetailPage />,
              },
            ],
          },
          {
            path: 'snapshots',
            children: [
              {
                index: true,
                element: <Navigate to="member" replace />,
              },
              {
                path: 'member',
                element: <MemberSnapshotView />,
              },
              {
                path: 'admin',
                element: (
                  <ProtectedTeamRoute allowedRoles={[TeamRole.Owner, TeamRole.Admin]}>
                    <AdminSnapshotView />
                  </ProtectedTeamRoute>
                ),
              },
              {
                path: 'shared',
                element: <SharedSnapshotView />,
              },
              {
                path: 'detail/:snapshotId',
                element: <SnapshotDetailPage />,
              },
              {
                path: 'member-detail/:snapshotId',
                element: <MemberSnapshotDetailPage />,
              },
            ],
          },
          {
            path: 'space',
            children: [
              {
                index: true,
                element: <Navigate to="member" replace />,
              },
              {
                path: 'member',
                element: <MemberSpaceView />,
              },
              {
                path: 'admin',
                element: (
                  <ProtectedTeamRoute allowedRoles={[TeamRole.Owner, TeamRole.Admin]}>
                    <AdminSpaceView />
                  </ProtectedTeamRoute>
                ),
              },
              {
                path: 'shared',
                element: (
                  <ProtectedTeamRoute allowedRoles={[TeamRole.Owner, TeamRole.Admin]}>
                    <TeamSharedSpaceView />
                  </ProtectedTeamRoute>
                ),
              },
              {
                path: 'member/:memberId',
                element: (
                  <ProtectedTeamRoute allowedRoles={[TeamRole.Owner, TeamRole.Admin]}>
                    <TeamMemberSpaceDetailPage />
                  </ProtectedTeamRoute>
                ),
              },
            ],
          },
          { path: 'deploy', element: <AutoDeploymentPage /> },
          { path: 'deploy/create', element: <DeploymentCreateSubPage /> },
          { path: 'eip', element: <EIPListPage /> },
          { path: 'eip/:eipResId/acl', element: <ACLRulesPage /> },
          {
            path: 'eip/:eipResId/acl/l3/new',
            element: <EIPL3RuleFormPage mode="create" />,
          },
          {
            path: 'eip/:eipResId/acl/l3/:l3RuleId/edit',
            element: <EIPL3RuleFormPage mode="edit" />,
          },
          {
            path: 'eip/:eipResId/acl/l3/:l3RuleId/l4/new',
            element: <EIPL4RuleFormPage mode="create" />,
          },
          {
            path: 'eip/:eipResId/acl/l3/:l3RuleId/l4/:l4RuleId/edit',
            element: <EIPL4RuleFormPage mode="edit" />,
          },
          // { path: 'scaling', element: <AutoScalingListPage /> },
          // { path: 'scaling/create', element: <ScalingCreateSubPage /> },
          // { path: 'scaling/monitor', element: <ScalingMonitorDetailSubPage /> },
        ],
      },
      {
        path: '',
        element: <Layout />,
        children: [
          { path: '', element: <Navigate to="/create" replace /> },
          { path: '/create', element: <CreateInstancePage /> },
          { path: '/instances', element: <InstanceListPage /> },
          {
            path: '/instances/monitor',
            element: <MonitorDetailPage />,
          },
          { path: '/model-route', element: <ModelRoutePage /> },
          { path: '/model-route/create', element: <NewModelRoutePage /> },
          { path: '/model-route/:routeResId/edit', element: <NewModelRoutePage /> },
          { path: '/model-route/billing', element: <RouteBillingDetailPage /> },
          { path: '/model-route/:routeResId/monitor', element: <RouteMonitorDetailPage /> },
          { path: '/data', element: <DataMgrPage /> },
          { path: '/scaling', element: <AutoScalingListPage /> },
          { path: '/deploy', element: <AutoDeploymentPage /> },
          { path: '/deploy/create', element: <DeploymentCreateSubPage /> },
          { path: '/scaling/create', element: <ScalingCreateSubPage /> },
          { path: '/scaling/monitor', element: <ScalingMonitorDetailSubPage /> },
          { path: '/snapshots', element: <SnapshotListPage /> },
          { path: '/image', element: <CustomImagePage /> },
          { path: '/mesh', element: <MeshClusteringListPage /> },
          { path: '/eip', element: <EIPListPage /> },
          { path: '/eip/:eipResId/acl', element: <ACLRulesPage /> },
          {
            path: '/eip/:eipResId/acl/l3/new',
            element: <EIPL3RuleFormPage mode="create" />,
          },
          {
            path: '/eip/:eipResId/acl/l3/:l3RuleId/edit',
            element: <EIPL3RuleFormPage mode="edit" />,
          },
          {
            path: '/eip/:eipResId/acl/l3/:l3RuleId/l4/new',
            element: <EIPL4RuleFormPage mode="create" />,
          },
          {
            path: '/eip/:eipResId/acl/l3/:l3RuleId/l4/:l4RuleId/edit',
            element: <EIPL4RuleFormPage mode="edit" />,
          },
          { path: '/space', element: <SpaceMgrPage /> },
          {
            path: 'private-region',
            element: <PrivateRegionPage />,
            children: [
              { index: true, element: <Navigate to="dashboard" replace /> },
              { path: 'dashboard', element: <DashboardView /> },
              { path: 'compute', element: <ComputeView /> },
              { path: 'monitor', element: <NodeMonitorView /> },
            ],
          },
          { path: '/billing', element: <BillingPage /> },
          { path: '/invoice', element: <InvoicePage /> },
          { path: '/profile', element: <ProfilePage /> },
          { path: '/profile/access-keys', element: <AccessKeysPage /> },
        ],
      },
      {
        path: '',
        element: <AuthLayout />,
        children: [
          {
            path: '/login',
            element: <LoginPage />,
          },
          {
            path: '/register',
            element: <RegisterPage />,
          },
        ],
      },
      {
        path: '',
        element: <PaymentCallbackLayout />,
        children: [
          {
            path: '/payment/callback/paypal',
            element: <PaypalPaymentCallbackPage />,
          },
          {
            path: '/payment/callback/paypal_cancel',
            element: <PaypalPaymentCancelPage />,
          },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
      import.meta.env.DEV && {
        path: 'dev',
        element: <DevPage />,
      },
    ].filter(Boolean) as any,
  },
])
